import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { es } from "date-fns/locale";
import { Plus, Trash2, Save, Loader2, CalendarOff } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Page, PageTitle, Loading } from "@/components/site/Common";
import { ApptBadge, CalendarExport, CheckIn, useApptRefresh } from "@/components/job/Appointment";
import { useMe } from "@/hooks/useMe";
import { get, put, errMsg } from "@/lib/api";
import { isoDate, longDate, todayIso, WEEKDAYS } from "@/lib/hz";

const toDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };

const ApptCard = ({ a, isPro, notes }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`agenda-appt-${a.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2"><span className="font-heading text-lg font-bold">{a.start} – {a.end}</span><ApptBadge status={a.status} /></div>
    <Link to={`/trabajos/${a.job_id}`} className="mt-1 block font-semibold hover:text-orange-600">{a.job_title}</Link>
    <div className="text-sm text-slate-500">{isPro ? a.client_name : a.pro_name}</div>
    {notes && Object.values(notes).some(Boolean) && <div className="mt-2 rounded-xl bg-amber-50 p-2 text-xs text-amber-900">{Object.values(notes).filter(Boolean).join(" · ")}</div>}
    <CalendarExport a={a} other={isPro ? a.client_name : a.pro_name} />
    {isPro && <CheckIn a={a} />}
  </div>
);

function Appointments({ isPro }) {
  const [month, setMonth] = useState(new Date());
  const [day, setDay] = useState(todayIso());
  const key = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
  const appts = useQuery({ queryKey: ["agenda", "month", key], queryFn: () => get(`/agenda/appointments?month=${key}`) });
  const pending = useQuery({ queryKey: ["agenda", "pending"], queryFn: () => get("/agenda/pending") });
  const plan = useQuery({ queryKey: ["agenda", "day-plan", day], queryFn: () => get(`/agenda/day-plan?date=${day}`), enabled: isPro, retry: false });
  const jobs = useQuery({ queryKey: ["jobs", "mine"], queryFn: () => get("/jobs/mine"), enabled: !isPro });
  const list = appts.isError ? [] : appts.data ?? [];
  const notesById = new Map((plan.data?.stops ?? []).map((s) => [s.appointment.id, s.notes]));
  const dayList = list.filter((a) => a.date === day).sort((x, y) => x.start.localeCompare(y.start));
  const booked = new Set(list.filter((a) => a.status !== "cancelada").map((a) => a.job_id));
  const needBooking = (jobs.data ?? []).filter((j) => ["asignado", "en_curso"].includes(j.status) && !booked.has(j.id));
  const pend = pending.isError ? [] : pending.data ?? [];
  const goTo = (iso) => { setDay(iso); setMonth(toDate(iso)); };

  return (
    <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
      <div className="rounded-3xl border border-slate-200 bg-white p-3" data-testid="agenda-calendar">
        <Calendar mode="single" locale={es} weekStartsOn={1} month={month} onMonthChange={setMonth} selected={toDate(day)}
          onSelect={(d) => d && setDay(isoDate(d))}
          modifiers={{ booked: list.filter((a) => a.status !== "cancelada").map((a) => toDate(a.date)) }}
          modifiersClassNames={{ booked: "font-bold text-orange-700 underline decoration-orange-500 decoration-2 underline-offset-4" }} />
      </div>
      <div className="space-y-5">
        <div>
          <h2 className="font-heading text-lg font-bold first-letter:uppercase" data-testid="agenda-selected-day">{longDate(day)}</h2>
          <div className="mt-3 space-y-3" data-testid="agenda-day-list">
            {appts.isLoading && <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />}
            {appts.isSuccess && !dayList.length && <p className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400" data-testid="agenda-day-empty">Sin citas este día.</p>}
            {dayList.map((a) => <ApptCard key={a.id} a={a} isPro={isPro} notes={notesById.get(a.id)} />)}
          </div>
        </div>
        {!!pend.length && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5" data-testid="agenda-pending">
            <h3 className="font-heading font-bold text-amber-900">{isPro ? "Solicitudes de cita por confirmar" : "Nuevos horarios propuestos"} ({pend.length})</h3>
            <div className="mt-3 space-y-2">
              {pend.map((a) => (
                <button key={a.id} type="button" onClick={() => goTo(a.date)} data-testid={`agenda-pending-${a.id}`} className="block w-full rounded-xl bg-white p-3 text-left text-sm transition-shadow hover:shadow-md">
                  <span className="block font-semibold first-letter:uppercase">{longDate(a.date)} · {a.start}</span>
                  <span className="block text-slate-500">{a.job_title} — {isPro ? a.client_name : a.pro_name}</span>
                  <Link to={`/trabajos/${a.job_id}`} className="mt-1 inline-block text-xs font-semibold text-orange-600">{isPro ? "Confirmar o proponer otro →" : "Aceptar o cambiar →"}</Link>
                </button>
              ))}
            </div>
          </div>
        )}
        {!isPro && !!needBooking.length && (
          <div className="rounded-3xl border border-orange-200 bg-orange-50 p-5" data-testid="agenda-need-booking">
            <h3 className="font-heading font-bold text-orange-900">Servicios pendientes de cita</h3>
            <div className="mt-3 space-y-2">
              {needBooking.map((j) => (
                <Link key={j.id} to={`/trabajos/${j.id}`} data-testid={`agenda-book-${j.id}`} className="block rounded-xl bg-white p-3 text-sm transition-shadow hover:shadow-md">
                  <span className="font-semibold">{j.title}</span><span className="block text-orange-700">Elegir día y hora con {j.pro_name} →</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Availability() {
  const q = useQuery({ queryKey: ["agenda", "availability"], queryFn: () => get("/agenda/availability") });
  const refresh = useApptRefresh();
  const [draft, setDraft] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const v = draft ?? q.data;
  if (q.isLoading || !v) return q.isError ? <p className="text-sm text-rose-600">{errMsg(q.error)}</p> : <Loading />;
  const set = (patch) => setDraft({ ...v, ...patch });
  const setDay = (i, slots) => set({ weekly: { ...v.weekly, [String(i)]: slots } });
  const save = async () => {
    setBusy(true);
    try { await put("/agenda/availability", draft); setDraft(null); refresh(); toast.success("Disponibilidad guardada"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="availability-editor">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-bold">Horario semanal</h2>
          <div className="flex items-center gap-2 text-sm text-slate-600">Duración de cada cita
            <Select value={String(v.slot_minutes)} onValueChange={(x) => set({ slot_minutes: Number(x) })}>
              <SelectTrigger className="h-9 w-24" data-testid="availability-slot-minutes"><SelectValue /></SelectTrigger>
              <SelectContent>{[30, 60, 90, 120, 180, 240].map((m) => <SelectItem key={m} value={String(m)}>{m >= 60 ? `${m / 60} h` : `${m} min`}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          {WEEKDAYS.map((name, i) => {
            const slots = v.weekly[String(i)] ?? [];
            return (
              <div key={name} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start" data-testid={`availability-day-${i}`}>
                <div className="w-28 pt-2 text-sm font-semibold">{name}</div>
                <div className="flex-1 space-y-2">
                  {!slots.length && <div className="pt-2 text-sm text-slate-400">No disponible</div>}
                  {slots.map((s, r) => (
                    <div key={r} className="flex items-center gap-2">
                      <Input type="time" step={900} value={s.start} className="h-9 w-28" data-testid={`availability-${i}-${r}-start`} onChange={(e) => setDay(i, slots.map((x, k) => (k === r ? { ...x, start: e.target.value } : x)))} />
                      <span className="text-slate-400">–</span>
                      <Input type="time" step={900} value={s.end} className="h-9 w-28" data-testid={`availability-${i}-${r}-end`} onChange={(e) => setDay(i, slots.map((x, k) => (k === r ? { ...x, end: e.target.value } : x)))} />
                      <Button variant="ghost" size="icon" aria-label="Quitar franja" data-testid={`availability-${i}-${r}-remove`} onClick={() => setDay(i, slots.filter((_, k) => k !== r))}><Trash2 /></Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="self-start" data-testid={`availability-${i}-add`} onClick={() => setDay(i, [...slots, slots.length ? { start: "16:00", end: "19:00" } : { start: "09:00", end: "14:00" }])}><Plus />Franja</Button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="space-y-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="blocked-dates">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold"><CalendarOff className="size-5 text-rose-600" />Días no disponibles</h2>
          <p className="mt-1 text-sm text-slate-500">Vacaciones, festivos o días libres: no se podrán reservar citas.</p>
          <div className="mt-3 flex gap-2">
            <Input type="date" min={todayIso()} value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-10" data-testid="blocked-date-input" />
            <Button variant="outline" disabled={!newDate || v.blocked_dates.includes(newDate)} data-testid="blocked-date-add-btn" onClick={() => { set({ blocked_dates: [...v.blocked_dates, newDate].sort() }); setNewDate(""); }}>Añadir</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {!v.blocked_dates.length && <span className="text-sm text-slate-400">Ningún día bloqueado.</span>}
            {v.blocked_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold capitalize text-rose-800" data-testid={`blocked-date-${d}`}>
                {longDate(d)}<button type="button" aria-label="Quitar" className="ml-1" onClick={() => set({ blocked_dates: v.blocked_dates.filter((x) => x !== d) })}>×</button>
              </span>
            ))}
          </div>
        </div>
        <Button size="lg" className="h-12 w-full rounded-xl" disabled={!draft || busy} onClick={save} data-testid="availability-save-btn">{busy ? <Loader2 className="animate-spin" /> : <Save />}{draft ? "Guardar cambios" : "Sin cambios"}</Button>
      </div>
    </div>
  );
}

export default function Agenda() {
  const { data: me } = useMe();
  const isPro = me.role === "profesional";
  return (
    <Page>
      <PageTitle eyebrow="Agenda" title={isPro ? "Mis citas y disponibilidad" : "Mis servicios y citas"}
        sub={isPro ? "Define cuándo trabajas y gestiona las citas que te solicitan." : "Consulta tus citas y elige día y hora para tus servicios asignados."} />
      <div className="mt-8">
        {isPro ? (
          <Tabs defaultValue="citas">
            <TabsList><TabsTrigger value="citas" data-testid="agenda-tab-appointments">Citas</TabsTrigger><TabsTrigger value="disponibilidad" data-testid="agenda-tab-availability">Disponibilidad</TabsTrigger></TabsList>
            <TabsContent value="citas" className="mt-6"><Appointments isPro /></TabsContent>
            <TabsContent value="disponibilidad" className="mt-6"><Availability /></TabsContent>
          </Tabs>
        ) : <Appointments isPro={false} />}
      </div>
    </Page>
  );
}
